/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, action, runInAction, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';

import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreLoading, isStoreError, isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import KeyPairCreateForm from '@amzn/key-pair-mgmt-ui/dist/parts/key-pairs/parts/KeyPairCreateForm';

import ScEnvironmentSshConnectionRow from './ScEnvironmentSshConnectionRow';

class ScEnvironmentSshConnections extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.showCreateKey = false;
    });
  }

  componentDidMount() {
    const store = this.keyPairsStore;
    if (!isStoreReady(store)) {
      swallowError(store.load());
    }
  }

  get isAppStreamEnabled() {
    return process.env.REACT_APP_IS_APP_STREAM_ENABLED === 'true';
  }

  get environment() {
    return this.props.scEnvironment;
  }

  get keyPairsStore() {
    return this.props.keyPairsStore;
  }

  get connections() {
    return this.environment.getConnections(item => item.scheme === 'ssh');
  }

  toggleCreateKey = () => {
    this.showCreateKey = !this.showCreateKey;
  };

  render() {
    const store = this.keyPairsStore;
    const env = this.environment;
    const state = env.state;
    const canConnect = state.canConnect;
    const empty = _.isEmpty(this.connections);

    if (!canConnect) return null;
    let content = null;

    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} className="pt2 mb2" />;
    } else if (isStoreLoading(store)) {
      content = <ProgressPlaceHolder segmentCount={1} className="mt2 mb2" />;
    } else if (empty) {
      content = this.renderEmpty();
    } else {
      content = this.renderConnections();
    }

    return <div className="fadeIn animated">{content}</div>;
  }

  renderAppStreamInfo() {
    return (
      <div className="connection-info-box" data-testid="appstream-instructions-ssh">
        <Icon name="info circle" />
        <div className="connection-info-box-content">
          <b>AppStream SSH Connection Instructions:</b>
          <ul>
            <li>Select your SSH key and click "Use this SSH Key"</li>
            <li>A private IP will be displayed for Putty connection</li>
            <li>Click "Connect" to open an AppStream session</li>
            <li>Copy your private SSH key to AppStream via Notepad</li>
            <li>Use PuttyGen to convert PEM to PPK format</li>
            <li>Connect using Putty with the private IP and PPK file</li>
          </ul>
          <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#6b7280' }}>
            Please allow popups for this domain.
          </div>
        </div>
      </div>
    );
  }

  renderConnections() {
    const env = this.environment;
    const showCreateKey = this.showCreateKey;
    const connections = this.connections;
    const store = this.keyPairsStore;
    const emptyKeys = store.empty;

    if (showCreateKey) return this.renderCreateKeyForm();

    return (
      <div className="connection-card">
        <div className="connection-card-header">
          <div className="connection-card-title">
            <Icon name="terminal" />
            SSH Connection (Command Line)
          </div>
          <button className="connection-btn connection-btn-secondary" onClick={this.toggleCreateKey}>
            <Icon name="plus" style={{ margin: 0, lineHeight: 1, height: '1em' }} /> Create SSH Key
          </button>
        </div>
        <div className="connection-card-body">
          <div className="connection-intro">
            SSH gives you command-line (terminal) access to your workspace. Select an SSH key below and click
            <b> Use this SSH Key</b> to activate the connection. You will have <b>60 seconds</b> to copy the SSH
            command and connect — after that, simply click the button again to get a new 60-second window.
            Once connected, your session stays open until you disconnect.
          </div>
          {this.isAppStreamEnabled && this.renderAppStreamInfo()}
          {emptyKeys && (
            <div className="connection-info-box" style={{ background: '#fef3c7', borderColor: '#fcd34d' }}>
              <Icon name="warning sign" style={{ color: '#92400e' }} />
              <div className="connection-info-box-content" style={{ color: '#92400e' }}>
                <b>No SSH Keys Found</b>
                <div>You need to create an SSH key before you can connect. Click "Create SSH Key" above.</div>
              </div>
            </div>
          )}
          {_.map(connections, item => (
            <ScEnvironmentSshConnectionRow key={item.id} scEnvironment={env} connectionId={item.id} />
          ))}
          <div className="connection-help-link">
            <Icon name="question circle outline" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
            Need help? Visit the <a href="/documentation">Documentation</a> for detailed guides.
          </div>
        </div>
      </div>
    );
  }

  renderCreateKeyForm() {
    return (
      <div className="connection-card">
        <div className="connection-card-header">
          <div className="connection-card-title">
            <Icon name="key" />
            Create New SSH Key
          </div>
          <button className="connection-btn connection-btn-secondary" onClick={this.toggleCreateKey}>
            <Icon name="arrow left" style={{ margin: 0, lineHeight: 1, height: '1em' }} /> Back
          </button>
        </div>
        <div className="connection-card-body">
          <KeyPairCreateForm onCancel={this.toggleCreateKey} />
        </div>
      </div>
    );
  }

  renderEmpty() {
    return (
      <div className="connection-card">
        <div className="connection-empty">
          <Icon name="terminal" />
          <div className="connection-empty-title">No SSH Connections</div>
          <div className="connection-empty-text">This workspace does not have any SSH connections.</div>
        </div>
      </div>
    );
  }
}

decorate(ScEnvironmentSshConnections, {
  environment: computed,
  keyPairsStore: computed,
  connections: computed,
  showCreateKey: observable,
  toggleCreateKey: action,
});

export default inject('keyPairsStore')(withRouter(observer(ScEnvironmentSshConnections)));
